import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  X, AlertOctagon, AlertTriangle, Info, ExternalLink,
  Shield, Ruler, Target, TrendingUp, User as UserIcon,
  Calendar as CalendarIcon, Tag, MapPin,
} from "lucide-react";
import ConfidenceBar from "@/components/ConfidenceBar";

// Enriched "Why this pill?" popover.
// Strictly read-only: explain trace, sources, affected entities, deep links
// and dismiss. No apply / no writes / no mutation of rota / leave / kpis.

const SEV = {
  critical: {
    tone: "bg-red-50 text-red-700 border-red-200",
    badge: "bg-red-100 text-red-700",
    label: "Critical",
    Icon: AlertOctagon,
  },
  warning: {
    tone: "bg-amber-50 text-amber-700 border-amber-200",
    badge: "bg-amber-100 text-amber-700",
    label: "Warning",
    Icon: AlertTriangle,
  },
  info: {
    tone: "bg-[#DBE7FE] text-[#1D4ED8] border-[#BFD3FB]",
    badge: "bg-[#DBE7FE] text-[#1D4ED8]",
    label: "Info",
    Icon: Info,
  },
};

const SOURCE_META = {
  rule: { Icon: Ruler, label: "Rule",
          link: (id) => `/app/rules#rule=${encodeURIComponent(id)}`,
          fallback: "/app/rules" },
  policy: { Icon: Shield, label: "Policy",
            link: (id, code) => {
              const tab = code === "leave_rule" ? "leave"
                : code === "fairness" ? "fairness" : "coverage";
              return `/app/policies#${tab}=${encodeURIComponent(id)}`;
            },
            fallback: "/app/policies" },
  kpi: { Icon: TrendingUp, label: "KPI",
         link: (id) => `/app/kpis#kpi=${encodeURIComponent(id)}`,
         fallback: "/app/kpis" },
  skill: { Icon: Target, label: "Skill",
           link: (id) => `/app/skills#skill=${encodeURIComponent(id)}`,
           fallback: "/app/skills" },
};

const AFFECTED_ICON = {
  employee: UserIcon,
  date: CalendarIcon,
  category: Tag,
  location: MapPin,
};

function uniqLinks(sources, code) {
  const seen = new Set();
  const out = [];
  for (const s of sources || []) {
    const meta = SOURCE_META[s.kind];
    if (!meta) continue;
    const key = `${s.kind}:${s.id || ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const to = s.id ? meta.link(s.id, code) : meta.fallback;
    out.push({ kind: s.kind, id: s.id, name: s.name || meta.label, to,
                Icon: meta.Icon, label: meta.label });
  }
  return out;
}

const MAX_EXPLAIN = 6;
const MAX_AFFECTED = 4;

export default function AdvisoryPill({ pill, onDismiss, testid }) {
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);
  const btnRef = useRef(null);

  const m = SEV[pill.severity] || SEV.info;
  const Icon = m.Icon;
  const links = uniqLinks(pill.sources, pill.code);
  const explain = Array.isArray(pill.explain) ? pill.explain : [];
  const explainShown = explain.slice(0, MAX_EXPLAIN);
  const explainMore = explain.length - explainShown.length;
  const affected = Array.isArray(pill.affected) ? pill.affected : [];
  const affectedShown = affected.slice(0, MAX_AFFECTED);
  const affectedMore = affected.length - affectedShown.length;

  // Close on Esc / outside-click while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onClick = (e) => {
      if (popRef.current && popRef.current.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const dismiss = async (e) => {
    e.stopPropagation();
    try {
      await api.post("/advisory/dismiss",
        { signature: pill.signature, ttl_days: 7 });
      onDismiss?.(pill.signature);
    } catch { /* ignore */ }
  };

  return (
    <div className="relative inline-block" data-testid={testid}>
      <button type="button" ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full border ${m.tone}`}>
        <Icon className="h-3 w-3" />
        <span className="normal-case tracking-normal truncate max-w-xs">
          {pill.title}
        </span>
        <span onClick={dismiss}
          data-testid={`${testid}-dismiss`}
          className="ml-1 hover:opacity-70 cursor-pointer">
          <X className="h-3 w-3" />
        </span>
      </button>

      {open && (
        <div ref={popRef}
             className="absolute z-30 mt-1 w-80 max-w-[90vw] p-3 rounded-lg border border-slate-200 bg-white shadow-lg"
             data-testid={`${testid}-popover`}>
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="font-medium text-sm text-[#0B1A36]">{pill.title}</div>
            <button type="button" onClick={() => setOpen(false)}
              data-testid={`${testid}-popover-close`}
              className="text-[#4B5773] hover:text-[#0B1A36]"
              aria-label="Close">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <span data-testid={`${testid}-severity`}
            className={`inline-block text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded ${m.badge}`}>
            {m.label}
          </span>

          {pill.message && (
            <p className="mt-2 text-xs text-[#4B5773]">{pill.message}</p>
          )}

          {explainShown.length > 0 && (
            <div className="mt-3">
              <div className="text-[11px] uppercase tracking-widest text-[#4B5773] mb-1">
                Why this fired
              </div>
              <ul data-testid={`${testid}-explain`}
                  className="list-disc list-outside ml-4 space-y-1 text-xs text-[#0B1A36]">
                {explainShown.map((e, i) => (
                  <li key={i} data-testid={`${testid}-explain-item-${i}`}>{e}</li>
                ))}
              </ul>
              {explainMore > 0 && (
                <div className="text-[11px] text-[#4B5773] mt-1">
                  +{explainMore} more reason{explainMore === 1 ? "" : "s"}
                </div>
              )}
            </div>
          )}

          <div className="mt-3">
            <div className="text-[11px] uppercase tracking-widest text-[#4B5773] mb-1">
              Confidence
            </div>
            <div title="Based on rule freshness, input coverage, conflicts and priority">
              <ConfidenceBar value={pill.confidence}
                testid={`${testid}-confidence`} />
            </div>
          </div>

          {links.length > 0 && (
            <div className="mt-3">
              <div className="text-[11px] uppercase tracking-widest text-[#4B5773] mb-1">
                Source{links.length > 1 ? "s" : ""}
              </div>
              <ul className="space-y-1">
                {links.map((l, i) => (
                  <li key={`${l.kind}-${l.id || i}`}
                      data-testid={`${testid}-source-${i}`}
                      className="text-xs text-[#0B1A36] flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 truncate">
                      <l.Icon className="h-3 w-3 text-[#4B5773]" />
                      <span className="text-[#4B5773]">{l.label}:</span>
                      <span className="truncate">{l.name}</span>
                    </span>
                    <Link to={l.to}
                      onClick={() => setOpen(false)}
                      data-testid={`${testid}-source-link-${i}`}
                      className="text-[11px] text-[#2563EB] hover:underline inline-flex items-center shrink-0">
                      Open<ExternalLink className="h-3 w-3 ml-0.5" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {affectedShown.length > 0 && (
            <div className="mt-3">
              <div className="text-[11px] uppercase tracking-widest text-[#4B5773] mb-1">
                Affected
              </div>
              <div className="flex flex-wrap gap-1">
                {affectedShown.map((a, i) => {
                  const AIcon = AFFECTED_ICON[a.kind] || Tag;
                  return (
                    <span key={`${a.kind}-${a.id || i}`}
                      data-testid={`${testid}-affected-${a.kind}`}
                      className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full bg-slate-100 text-[#0B1A36]">
                      <AIcon className="h-3 w-3 text-[#4B5773]" />
                      {a.name || a.id}
                    </span>
                  );
                })}
                {affectedMore > 0 && (
                  <span className="text-[11px] text-[#4B5773] inline-flex items-center px-1">
                    +{affectedMore} more
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-end">
            <button type="button" onClick={dismiss}
              data-testid={`${testid}-dismiss-7d`}
              className="text-[11px] text-[#4B5773] hover:text-[#0B1A36]">
              Dismiss 7d
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
